import { useState } from 'react';
import type { AlertChannel, AlertChannelIn } from '@glyph/protocol-ts';
import { useAlertChannels, useMe } from '@glyph/core';
import { Button, Card, CardHeader, Dialog, EmptyState, IconButton, Note, Select, SwitchRow, TextInput, useToast } from '@glyph/ui';

// Alert routing is the owner's since 2026-10-23 (the cloud refuses admins).
const MANAGE_ROLES = new Set(['owner', 'platform']);
const SEVERITY_OPTIONS = [
  { value: 'info', label: 'Everything' },
  { value: 'warning', label: 'Warnings and up' },
  { value: 'critical', label: 'Critical only' },
] as const;
const sevLabel = (s: AlertChannel['min_severity']) => SEVERITY_OPTIONS.find((o) => o.value === s)?.label.toLowerCase() ?? s;

type Draft = { id: string | null; kind: 'email' | 'webhook'; name: string; to: string; url: string; secret: string; min_severity: AlertChannel['min_severity'] };
const blank = (kind: 'email' | 'webhook'): Draft => ({ id: null, kind, name: kind === 'email' ? 'Ops inbox' : 'Webhook', to: '', url: '', secret: '', min_severity: 'warning' });
const toBody = (d: Draft, enabled: boolean): AlertChannelIn => ({
  kind: d.kind,
  name: d.name.trim() || (d.kind === 'email' ? 'Email' : 'Webhook'),
  config: d.kind === 'email' ? { to: d.to.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean) } : { url: d.url.trim(), secret: d.secret },
  enabled,
  min_severity: d.min_severity,
});
const valid = (d: Draft) => (d.kind === 'email' ? d.to.split(/[\s,;]+/).some((x) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x)) : /^https:\/\/.+/.test(d.url.trim()));

/**
 * Where alerts go besides the console: email addresses (sent through Resend) and webhooks. Each row is a real
 * routing target on the server: the switch persists, Test sends a message now, × removes it. The on-device OLED
 * always shows alerts and is not a row here. No SMS: there is no SMS provider.
 */
/** The recipients and webhook URLs are the owner's business: nobody else sees the card or loads the list (the API
 * refuses it too). */
export function RoutingCard() {
  const me = useMe();
  return MANAGE_ROLES.has(me.data?.role ?? '') ? <RoutingCardBody /> : null;
}

function RoutingCardBody() {
  const ch = useAlertChannels();
  const toast = useToast();
  const canManage = true;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<AlertChannel | null>(null);
  const rows = ch.data ?? [];
  const emailOff = ch.delivery && !ch.delivery.email_configured;
  const toggle = async (c: AlertChannel, on: boolean) => {
    try {
      await ch.update({ id: c.id, body: { kind: c.kind, name: c.name, config: c.config, enabled: on, min_severity: c.min_severity } });
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const save = async () => {
    if (!draft || !valid(draft)) return;
    try {
      const body = toBody(draft, true);
      if (draft.id) await ch.update({ id: draft.id, body: { ...body, enabled: rows.find((r) => r.id === draft.id)?.enabled ?? true } });
      else await ch.create(body);
      setDraft(null);
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const test = async (c: AlertChannel) => {
    try {
      const r = await ch.test(c.id);
      toast.push(r.sent ? `Test email ${r.detail}` : `Not sent: ${r.detail}`, r.sent ? 'ok' : 'warn');
    } catch (e) {
      toast.push((e as Error).message, 'bad');
    }
  };
  const edit = (c: AlertChannel) => setDraft({ id: c.id, kind: c.kind, name: c.name, to: (c.config.to ?? []).join(', '), url: c.config.url ?? '', secret: c.config.secret ?? '', min_severity: c.min_severity });
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const describe = (c: AlertChannel) => `${c.kind === 'email' ? (c.config.to ?? []).join(', ') || 'no address' : c.config.url || 'no url'} · ${sevLabel(c.min_severity)}${c.disabled_at ? ' · PAUSED AFTER FAILURES' : ''}`;
  return (
    <Card>
      <CardHeader title="Routing" meta={rows.filter((r) => r.enabled && !r.disabled_at).length ? `${rows.filter((r) => r.enabled && !r.disabled_at).length} ON` : 'CONSOLE ONLY'} />
      {emailOff && <Note className="block border-t border-line px-4 py-3">Email delivery is not set up on the server yet: a Resend API key and a sender on a verified domain are needed. Addresses below are kept, nothing is sent until then.</Note>}
      {rows.length === 0 && !ch.isLoading && <EmptyState label="Alerts stay in the console and on the units' screens" />}
      {rows.map((c) => (
        <SwitchRow
          key={c.id}
          title={c.name}
          hint={describe(c)}
          checked={c.enabled && !c.disabled_at}
          disabled={!canManage || ch.busy}
          onChange={(v) => void toggle(c, v)}
          trailing={
            canManage ? (
              <span className="flex items-center gap-1">
                {c.kind === 'email' && (
                  <Button size="xs" mono onClick={() => void test(c)} disabled={ch.testing || !!emailOff}>
                    TEST
                  </Button>
                )}
                <Button size="xs" mono onClick={() => edit(c)}>
                  EDIT
                </Button>
                <IconButton framed={false} danger label={`Remove ${c.name}`} className="text-xl text-fg5" onClick={() => setRemoving(c)}>
                  ×
                </IconButton>
              </span>
            ) : undefined
          }
          className="md:px-4"
        />
      ))}
      <SwitchRow title="On-device screen" hint="always · cannot be turned off" checked trailing={<span className="meta text-fg3">FIXED</span>} className="md:px-4" />
      {canManage && (
        <div className="flex flex-wrap gap-2 border-t border-line px-4 py-3">
          <Button size="responsive" onClick={() => setDraft(blank('email'))}>
            Add email
          </Button>
          <Button size="responsive" onClick={() => setDraft(blank('webhook'))}>
            Add webhook
          </Button>
        </div>
      )}
      <Dialog
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? `Edit ${draft.kind}` : draft?.kind === 'email' ? 'Email alerts to' : 'Webhook'}
        meta={draft?.kind === 'email' ? 'ONE OR MORE ADDRESSES · SENT THROUGH RESEND' : 'HTTPS · JSON · HMAC SIGNED'}
        confirm={{ label: draft?.id ? 'Save' : 'Add', onClick: save, disabled: !draft || !valid(draft), busy: ch.busy }}
      >
        {draft && (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Name</span>
              <TextInput value={draft.name} onChange={(e) => set({ name: e.target.value })} block />
            </label>
            {draft.kind === 'email' ? (
              <label className="flex flex-col gap-2">
                <span className="micro text-fg3">Addresses</span>
                <TextInput value={draft.to} onChange={(e) => set({ to: e.target.value })} block autoFocus placeholder="ops@example.com, stage@example.com" inputMode="email" autoCapitalize="none" />
              </label>
            ) : (
              <>
                <label className="flex flex-col gap-2">
                  <span className="micro text-fg3">URL</span>
                  <TextInput variant="mono" value={draft.url} onChange={(e) => set({ url: e.target.value })} block autoFocus placeholder="https://hooks.example.com/glyph" />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="micro text-fg3">Signing secret</span>
                  <TextInput variant="mono" value={draft.secret} onChange={(e) => set({ secret: e.target.value })} block placeholder="optional" />
                </label>
              </>
            )}
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Send</span>
              <Select value={draft.min_severity} onChange={(v) => set({ min_severity: v as AlertChannel['min_severity'] })} options={[...SEVERITY_OPTIONS]} block />
            </label>
          </div>
        )}
      </Dialog>
      <Dialog open={!!removing} onClose={() => setRemoving(null)} title={removing ? `Remove ${removing.name}?` : ''} confirm={{ label: 'Remove', destructive: true, onClick: async () => { if (removing) await ch.remove(removing.id); setRemoving(null); }, busy: ch.busy }}>
        Alerts stop going there at once. The console and the units' screens are unaffected.
      </Dialog>
    </Card>
  );
}
